import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { cx, useRuntimeCss } from '../../../shared/styles/runtimeCss';
import WebsiteSectionsCanvas from '../rendering/WebsiteSectionsCanvas';
import WebsiteBuilderSectionEditor from './WebsiteBuilderSectionEditor';

export const SECTION_TYPE_TRANSLATIONS = {
  hero: 'Въведение',
  countdown: 'Обратно броене',
  text: 'Текст',
  timeline: 'Хронология',
  gallery: 'Галерия',
  emailForm: 'Имейл Формуляр',
  weddingPhotoGallery: 'Сватбена фотогалерия',
  bestMenAndMaids: 'Кумове',
};

// Icon aliases removed — using simple glyphs in the builder tabs.

function getSectionTypeLabel(sectionType) {
  return SECTION_TYPE_TRANSLATIONS[sectionType] || sectionType;
}

function SortableSectionTab({ section, activeSection, onSelect, onRemove }) {
  const css = useRuntimeCss();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: section.id });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cx(
        'section-tab',
        activeSection === section.id ? 'active' : '',
        css({
          transform: transform
            ? `translate3d(${transform.x}px, ${transform.y}px, 0) scaleX(${transform.scaleX}) scaleY(${transform.scaleY})`
            : undefined,
          transition,
        })
      )}
      onClick={() => onSelect(section.id)}
    >
      <span className="section-icon">📄</span>
      {getSectionTypeLabel(section.type)}
      <button
        className="remove-section-button"
        aria-label={`Премахни ${getSectionTypeLabel(section.type)}`}
        onClick={(event) => {
          event.stopPropagation();
          onRemove(section.id);
        }}
      >
        ×
      </button>
    </div>
  );
}

function TemplateGrid({ category, templates, selectedTemplateId, onSelectTemplate }) {
  return (
    <div className="template-category">
      <h3 className="category-title">{category}</h3>
      <div className="template-grid">
        {templates.map((template) => (
          <div
            key={template.id}
            className={`template-card ${selectedTemplateId === template.id ? 'selected' : ''}`}
            onClick={() => onSelectTemplate(template)}
          >
            <div className="template-preview">
              <img src={template.previewImage} alt={template.name} className="template-preview-image" />
            </div>
            <h4>{template.name}</h4>
            <p>{template.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TemplateSelectionStep({
  websiteTitle,
  selectedTemplate,
  templateCategories,
  getTemplatesByCategory,
  onTitleChange,
  onSelectTemplate,
  onNext,
}) {
  return (
    <div className="builder-step">
      <h2>Стъпка 1: Избери темплейт и Заглавие</h2>
      <div className="website-info">
        <div className="form-group">
          <label>Заглавие на уебсайт</label>
          <input
            type="text"
            value={websiteTitle}
            onChange={(event) => onTitleChange(event.target.value)}
            placeholder="Въведи заглавието на твоя уебсайт"
          />
        </div>
      </div>
      <div className="template-categories">
        {Object.values(templateCategories).map((category) => (
          <TemplateGrid
            key={category}
            category={category}
            templates={getTemplatesByCategory(category)}
            selectedTemplateId={selectedTemplate?.id}
            onSelectTemplate={onSelectTemplate}
          />
        ))}
      </div>
      <div className="navigation-buttons">
        <button className="button next" onClick={onNext}>
          Напред
        </button>
      </div>
    </div>
  );
}

function SectionList({ sections, activeSection, setActiveSection, removeSection }) {
  return (
    <div className="section-tabs">
      {sections.map((section) => (
        <SortableSectionTab
          key={section.id}
          section={section}
          activeSection={activeSection}
          onSelect={setActiveSection}
          onRemove={removeSection}
        />
      ))}
    </div>
  );
}

function AddSectionButtons({ componentsMap, onAddSection }) {
  return (
    <div className="add-section">
      <h4>Добави нова секция</h4>
      <div className="component-buttons">
        {Object.keys(componentsMap).map((componentType) => (
          <button
            key={componentType}
            className="button secondary builder-add-section-button"
            onClick={() => onAddSection(componentType)}
          >{`Добави ${getSectionTypeLabel(componentType)}`}</button>
        ))}
      </div>
    </div>
  );
}

function BuilderSectionNavigation({
  sensors,
  onDragEnd,
  sections,
  activeSection,
  setActiveSection,
  removeSection,
  componentsMap,
  onAddSection,
  sectionEditorProps,
}) {
  return (
    <div className="config-panel">
      <div className="section-navigation">
        <h3>Секции</h3>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={sections.map((section) => section.id)} strategy={verticalListSortingStrategy}>
            <SectionList
              sections={sections}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              removeSection={removeSection}
            />
          </SortableContext>
        </DndContext>
        <AddSectionButtons componentsMap={componentsMap} onAddSection={onAddSection} />
      </div>
      <WebsiteBuilderSectionEditor {...sectionEditorProps} />
    </div>
  );
}

function BuilderPreviewPanel({
  sections,
  theme,
  customCSS,
  onPreviewWeddingGalleryPhotoUpload,
  onPreviewBestMenAndMaidsPhotoUpload,
}) {
  const css = useRuntimeCss();
  const previewContainerClassName = cx(
    'preview-container',
    css({
      backgroundColor: theme?.colors?.background,
      color: theme?.colors?.text,
    })
  );

  return (
    <div className="preview-panel">
      <div className="preview-header">
        <h3>Преглед</h3>
      </div>
      <div className={previewContainerClassName}>
        <WebsiteSectionsCanvas
          sections={sections}
          theme={theme}
          mode="preview"
          customCss={customCSS}
          canvasClassName="preview-scroll"
          onWeddingGalleryPhotoUpload={onPreviewWeddingGalleryPhotoUpload}
          onBestMenAndMaidsPhotoUpload={onPreviewBestMenAndMaidsPhotoUpload}
        />
      </div>
    </div>
  );
}

export function ComposerStep({
  sensors,
  onDragEnd,
  sections,
  activeSection,
  setActiveSection,
  removeSection,
  addSection,
  componentsMap,
  sectionEditorProps,
  theme,
  customCSS,
  websiteTitle,
  onSave,
  onBack,
  onPreviewWeddingGalleryPhotoUpload,
  onPreviewBestMenAndMaidsPhotoUpload,
}) {
  return (
    <div className="builder-step">
      <h2>Стъпка 2: Създай своя уебсайт</h2>
      <div className="content-layout">
        <BuilderSectionNavigation
          sensors={sensors}
          onDragEnd={onDragEnd}
          sections={sections}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          removeSection={removeSection}
          componentsMap={componentsMap}
          onAddSection={addSection}
          sectionEditorProps={sectionEditorProps}
        />
        <BuilderPreviewPanel
          sections={sections}
          theme={theme}
          customCSS={customCSS}
          onPreviewWeddingGalleryPhotoUpload={onPreviewWeddingGalleryPhotoUpload}
          onPreviewBestMenAndMaidsPhotoUpload={onPreviewBestMenAndMaidsPhotoUpload}
        />
      </div>
      <div className="navigation-buttons">
        <button className="button back" onClick={onBack}>Назад</button>
        <button className="button next" onClick={onSave} disabled={!websiteTitle}>Запази и Създай QR код</button>
      </div>
    </div>
  );
}

export function SuccessStep({ qrCodeUrl, isEditMode, onCopyUrl, onBackToDashboard, onCreateNew }) {
  return (
    <div className="builder-step">
      <h2>Вашият уебсайт е {isEditMode ? 'Актуализиран' : 'Готов'}!</h2>
      <div className="qr-code-container">
        <h3>Вашият персонализиран QR код</h3>
        <div className="qr-code">
          <QRCodeSVG
            value={qrCodeUrl}
            size={window.innerWidth <= 480 ? 160 : 180}
            bgColor="#ffffff"
            fgColor="#000000"
            level="H"
            includeMargin
          />
        </div>
        <p className="qr-code-url">{qrCodeUrl}</p>
        <button className="button secondary" onClick={onCopyUrl}>Копиране на URL</button>
      </div>
      <div className="navigation-buttons">
        <button className="button primary" onClick={onBackToDashboard}>Назад</button>
        <button className="button secondary" onClick={onCreateNew}>Нов уебсайт</button>
      </div>
    </div>
  );
}
